/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, no-restricted-properties */
import Controller from '@ember/controller';
import { isArray } from '@ember/array';
import { get, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import moment from 'moment';
import {
  filterBy,
  mapBy,
  uniqBy,
  without,
} from '@linc-technologies/linced-common/utils/arrays';

export default class AbsencesController extends Controller {
  @service
  attendanceCodes;

  @service
  session;

  @service
  dates;

  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @alias('model.date')
  dateObj;

  @alias('dateObj.date')
  date;

  @computed('date')
  get dateFormatted() {
    return moment(this.date, 'YYYY-MM-DD').format('D MMM YYYY');
  }

  @computed('date', 'dates.structures.[]')
  get day() {
    return this.dates.dayForDate(this.date);
  }

  @computed('day.periods.length')
  get hasMultiplePeriods() {
    let numPeriods = this.get('day.periods.length');
    return !isEmpty(numPeriods) && numPeriods > 1;
  }

  @computed('model.{records.@each.id,period.id}')
  get records() {
    // The extra filter on person.id is to ensure that we only look at valid
    // records with a PersonId corresponding to a Person we have access to.
    return this.get('model.records').filter(
      (item) =>
        item.period_id === this.get('model.period.id') && item.person_id,
    );
  }

  @computed('records.@each.code', 'trackMovements')
  get absences() {
    if (this.trackMovements) {
      // Code Given list doesn't apply to schools which track Movements.
      return [];
    }

    let absentCodes = mapBy(
      filterBy(this.get('attendanceCodes.allCodes'), 'inClass', false),
      'code',
    );
    let records = this.records;

    // No movement tracking - just filter records by code.
    if (isArray(records)) {
      return records.filter((record) => {
        let code = record.get('code');
        return absentCodes.includes(code);
      });
    } else {
      return [];
    }
  }

  @alias('absences.length')
  numAbsences;

  @computed('attendanceCodes.allCodes.@each.{code,label}')
  get codeToLabel() {
    return this.attendanceCodes.allCodes.reduce((map, code) => {
      map[code.code] = code.label;
      return map;
    }, {});
  }

  movementColumns = [
    {
      label: 'Last Name',
      path: 'person.lastName',
      filter: '',
    },
    {
      label: 'First Name',
      path: 'person.firstName',
      filter: '',
    },
    {
      label: 'Class',
      path: 'person.homeroom.label',
      filter: '',
    },
    {
      label: 'Type',
      path: 'type',
      filter: '',
    },
    {
      label: 'Reason',
      path: 'reason',
      filter: '',
    },
    {
      label: 'From',
      path: 'fromTime',
      filter: '',
    },
    {
      label: 'To',
      path: 'toTime',
      filter: '',
    },
    {
      label: 'Comment',
      path: 'notes',
      filter: '',
    },
  ];

  @computed('attendanceCodes.{allCodes.[],present}')
  get absentCodes() {
    return mapBy(
      without(
        this.get('attendanceCodes.allCodes'),
        this.get('attendanceCodes.present'),
      ),
      'code',
    );
  }

  @computed('records.@each.code', 'trackMovements')
  get movements() {
    if (!this.trackMovements) {
      // Movement log doesn't apply to schools which don't track Movements.
      return [];
    }

    let absentCodes = this.absentCodes;
    let presentCode = this.get('attendanceCodes.present.code');
    let unknownCode = this.get('attendanceCodes.unknown.code');
    let periodName = this.get('model.period.longName');
    let records = this.records;

    return records.reduce((rows, record) => {
      if (!isEmpty(record.movements)) {
        // We have a movement log.  Use it.
        record.movements.forEach((movement, index) => {
          if (absentCodes.includes(movement.code)) {
            let nextMovement = record.movements.objectAt(index + 1);

            let lateArrival =
              index === 0 &&
              !isEmpty(nextMovement) &&
              nextMovement.code === presentCode;
            let earlyLeaver =
              index > 0 &&
              index === record.movements.length - 1 &&
              record.movements.objectAt(index - 1).code === presentCode &&
              movement.code === unknownCode;
            let wholePeriod = record.movements.length === 1;

            let type = 'Timed';
            switch (true) {
              case lateArrival:
                type = 'Late';
                break;
              case earlyLeaver:
                type = 'Early';
                break;
              case wholePeriod:
                type = periodName;
                break;
            }

            let toTime;
            if (!isEmpty(nextMovement)) {
              toTime = nextMovement.time;
            }
            rows.push({
              id: `${record.id}.${index}`,
              person: record.person,
              record,
              truancyClass: this.attendanceCodes.truancyClasses[movement.code],
              code: movement.code,
              label: this.codeToLabel[movement.code],
              reason: `${movement.code} - ${this.codeToLabel[movement.code]}`,
              fromTime: moment.unix(movement.time).format('h:mm a'),
              toTime: toTime ? moment.unix(toTime).format('h:mm a') : '',
              type,
              notes: movement.notes,
            });
          }
        });
      } else {
        // No movement log - data predates movement logs?
        if (absentCodes.includes(record.code) && record.code !== unknownCode) {
          rows.push({
            id: record.id,
            person: record.person,
            record,
            truancyClass: this.attendanceCodes.truancyClasses[record.code],
            code: record.code,
            label: this.codeToLabel[record.code],
            reason: `${record.code} - ${this.codeToLabel[record.code]}`,
            fromTime: record.createTime,
            notes: record.notes,
          });
        }
      }
      return rows;
    }, []);
  }

  @computed('movements.[]', 'movementColumns.@each.filter')
  get filteredMovements() {
    let filters = filterBy(this.movementColumns, 'filter');
    if (isEmpty(filters)) {
      // No active filters - just return the unfiltered list.
      return this.movements;
    }

    return this.movements.filter((movement) => {
      return filters.every((filter) => {
        let value = get(movement, filter.path);
        if (filter.path === 'reason') {
          let onlyCodes = filter.filter
            .toUpperCase()
            .split('||')
            .every((subFilter) => {
              return this.absentCodes.includes(
                subFilter.trim().replace(/^!/, ''),
              );
            });
          if (onlyCodes) {
            // Allow single-character filters on reason to match the code only.
            value = movement.code;
          }
        }
        return filterValue(value, DataType.STRING, filter.filter);
      });
    });
  }

  @alias('movements.length')
  numMovements;

  @computed('movements')
  get numMovementStudents() {
    return uniqBy(this.movements, 'person.id').length;
  }

  @action
  refreshRecords() {
    this.model.records.update();
  }
}
